import React, {useEffect, useMemo, useState} from 'react'
import {Button, Form, Table} from 'react-bootstrap'
import {ToastContainer} from 'react-toastify'
import {useNavigate, useParams} from 'react-router-dom'
import {fetchTable} from '../../../api/tableRS'
import {saveTablePromet} from '../../../api/tablePromet'
import {ponistiMojeZaduzenjeZaTraku, vratiNalogeZaZaduzenuTraku} from '../../../api/radniNalozi'
import notifyToast from '../../../components/Toast/Toast'
import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent'
import LokacijeRsTraka from '../../../components/LokacijeRSTraka/LokacijeRsTraka'

const normalizeResponseData = (response: any) => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.data)) return response.data
    if (Array.isArray(response?.data?.data)) return response.data.data
    return []
}

const parseNumber = (value: any) => {
    if (typeof value === 'number') return value
    const normalized = String(value ?? '').trim().replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : 0
}

const getRadniNalogStavka = (item: any) =>
    item?.radni_nalog_stavka
    ?? item?.fmk_stavka?.radni_nalog_stavka
    ?? item?.fmk_stavka?.radni_nalog_stavk
    ?? item?.radni_nalog_stavk

const getStavkaId = (item: any) => {
    const radniNalogStavka = getRadniNalogStavka(item)
    return item?.IdStavke
        ?? item?.IdStavkeFMK
        ?? item?.Stavka
        ?? radniNalogStavka?.Id
        ?? radniNalogStavka?.id
        ?? item?.Id
        ?? item?.id
}

const getBrojNaloga = (item: any) => {
    const radniNalogStavka = getRadniNalogStavka(item)
    return radniNalogStavka?.BrojNaloga
        ?? radniNalogStavka?.brojNaloga
        ?? item?.BrojNaloga
        ?? item?.brojNaloga
        ?? '-'
}

const getSifraArtikla = (item: any) => {
    const radniNalogStavka = getRadniNalogStavka(item)
    return radniNalogStavka?.SifraArtikla
        ?? radniNalogStavka?.sifraArtikla
        ?? item?.SifraArtikla
        ?? item?.sifraArtikla
        ?? '-'
}

const getNazivArtikla = (item: any) => {
    const radniNalogStavka = getRadniNalogStavka(item)
    return radniNalogStavka?.NazivArtikla
        ?? radniNalogStavka?.nazivArtikla
        ?? item?.NazivArtikla
        ?? item?.nazivArtikla
        ?? '-'
}

const getRowKey = (item: any, index: number) => String(item?.Id ?? item?.id ?? `${getStavkaId(item)}-${index}`)

const ZaduzeniMaterijalTraka: React.FC = () => {
    const {id} = useParams<{id: string}>()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [trakaDetalji, setTrakaDetalji] = useState<any>()
    const [stavke, setStavke] = useState<any[]>([])
    const [krajnjaTezina, setKrajnjaTezina] = useState('')
    const [lokacija, setLokacija] = useState<string>('')
    const [napomena, setNapomena] = useState('')
    const [nalogSeRadiOdViseRolni, setNalogSeRadiOdViseRolni] = useState(false)
    const [utrosakMaterijala, setUtrosakMaterijala] = useState<Record<string, string>>({})
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        const loadData = async () => {
            if (!id) return

            setLoading(true)
            try {
                const [trakaResponse, zaduzenjaResponse] = await Promise.all([
                    fetchTable(Number(id)),
                    vratiNalogeZaZaduzenuTraku({IdTrake: id}),
                ])

                setTrakaDetalji(trakaResponse)
                setStavke(normalizeResponseData(zaduzenjaResponse))
            } catch (error: any) {
                notifyToast(false, error?.response?.data?.message ?? error.message ?? 'Greska pri ucitavanju trake.')
            } finally {
                setLoading(false)
            }
        }

        loadData()
    }, [id])

    const potrebnaKolicina = useMemo(() => {
        return stavke.reduce((sum, item) => sum + parseNumber(item?.PotrebnaKolicina), 0)
    }, [stavke])

    const trenutnaTezina = parseNumber(trakaDetalji?.Tezina)
    const krajnjaTezinaNumber = parseNumber(krajnjaTezina)
    const potrosenaKolicina = krajnjaTezina ? trenutnaTezina - krajnjaTezinaNumber : 0
    const odstupanje = potrebnaKolicina > 0 ? Math.abs(potrosenaKolicina - potrebnaKolicina) / potrebnaKolicina : 0
    const nemaPotrebneKolicinePoNormativu = stavke.length > 0 && potrebnaKolicina <= 0
    const prikaziUpozorenje = Boolean(krajnjaTezina) && potrebnaKolicina > 0 && odstupanje > 0.02
    const prikaziNapomenu = !nalogSeRadiOdViseRolni && (prikaziUpozorenje || nemaPotrebneKolicinePoNormativu)
    const komentarObavezan = prikaziNapomenu && !napomena.trim()

    useEffect(() => {
        if (!krajnjaTezina || !stavke.length || potrebnaKolicina <= 0) {
            setUtrosakMaterijala({})
            return
        }

        let allocated = 0
        const roundedPotrosenaKolicina = Math.round(potrosenaKolicina)
        const nextValues = stavke.reduce<Record<string, string>>((acc, item, index) => {
            const key = getRowKey(item, index)
            const potrebna = parseNumber(item?.PotrebnaKolicina)
            const isLast = index === stavke.length - 1
            const value = isLast
                ? roundedPotrosenaKolicina - allocated
                : Math.round((potrebna / potrebnaKolicina) * roundedPotrosenaKolicina)

            allocated += value
            acc[key] = String(value)
            return acc
        }, {})

        setUtrosakMaterijala(nextValues)
    }, [krajnjaTezina, potrebnaKolicina, potrosenaKolicina, stavke])

    const stavkeSaKolicinama = useMemo(() => {
        let runningKolicina = Math.round(trenutnaTezina)

        return stavke.map((item, index) => {
            const rowKey = getRowKey(item, index)
            const utrosak = utrosakMaterijala[rowKey] ?? ''
            const pocetnaKolicina = runningKolicina
            const krajnjaKolicina = utrosak !== '' ? pocetnaKolicina - parseNumber(utrosak) : ''

            if (krajnjaKolicina !== '') {
                runningKolicina = krajnjaKolicina
            }

            return {item, rowKey, pocetnaKolicina, utrosak, krajnjaKolicina}
        })
    }, [stavke, trenutnaTezina, utrosakMaterijala])

    const handlePotvrdiPromet = async () => {
        if (!krajnjaTezina || !lokacija) {
            notifyToast(false, 'Unesite krajnju tezinu trake i lokaciju.')
            return
        }

        if (!stavkeSaKolicinama.length) {
            notifyToast(false, 'Nema stavki za promet trake.')
            return
        }

        if (komentarObavezan) {
            notifyToast(false, 'Unesite napomenu za odstupanje.')
            return
        }

        const invalidRow = stavkeSaKolicinama.find(({krajnjaKolicina, utrosak}) => utrosak === '' || krajnjaKolicina === '')
        if (invalidRow) {
            notifyToast(false, 'Provjerite utrosak materijala i krajnju kolicinu na svim stavkama.')
            return
        }

        setSaving(true)

        try {
            for (const {item, krajnjaKolicina} of stavkeSaKolicinama) {
                const payload = {
                    TrakaID: Number(id),
                    NaKolicinu: Math.round(Number(krajnjaKolicina)),
                    BrojListe: getBrojNaloga(item),
                    Napomena: prikaziNapomenu ? napomena.trim() : null,
                    VanTolerancije: prikaziNapomenu ? 1 : 0,
                    KomentarNaToleranciju: prikaziNapomenu ? napomena.trim() : null,
                    Skenirano: Number(item?.Skenirano ?? item?.Skrenirano ?? 0),
                    Lokacija: lokacija,
                }

                const response: any = await saveTablePromet(payload)
                if (response?.error !== 0) {
                    throw new Error(response?.message || 'Greska pri kreiranju prometa trake.')
                }
            }

            await ponistiMojeZaduzenjeZaTraku({IdTrake: id})

            notifyToast(true, 'Promet trake uspjesno kreiran.')
            setTimeout(() => {
                navigate('/terminali/zaduzeni-materijal', {replace: true})
            }, 1500)
        } catch (error: any) {
            notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greska pri kreiranju prometa trake.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className='container-xxl px-0'>
            <ToastContainer />

            {loading ? (
                <LoadingComponent />
            ) : (
                <>
                    <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mb-4'>
                        <div className='min-w-0'>
                            <h1 className='fw-bolder mb-1 text-break'>
                                Promet trake: <span className='text-success'>{id}</span>
                            </h1>
                            <div className='text-muted'>Unesi krajnju tezinu trake i odaberi lokaciju.</div>
                        </div>

                        <Button variant='outline-secondary' className='px-4 w-100 w-md-auto' onClick={() => navigate(-1)}>
                            Nazad
                        </Button>
                    </div>

                    <div className='row g-3 mb-4'>
                        <div className='col-12 col-sm-6 col-xl-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body p-4'>
                                    <div className='text-muted small'>Traka ID</div>
                                    <div className='fw-bolder text-primary text-break' style={{fontSize: '2.25rem', lineHeight: 1.1}}>
                                        T-{id}
                                    </div>
                                    <div className='text-muted small mt-2'>Nastala od rolne</div>
                                    <div className='fs-4 fw-bolder'>R-{trakaDetalji?.BrojRolne || '-'}</div>
                                    <div className='text-muted small mt-2'>Boja</div>
                                    <div className='fs-5 fw-bolder text-danger'>
                                        {trakaDetalji?.repromaterijal?.boja_lica?.BojaNaziv
                                            ?? trakaDetalji?.boja_lica?.BojaNaziv
                                            ?? '-'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='col-12 col-sm-6 col-xl-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body p-4'>
                                    <div className='text-muted small'>Sirina</div>
                                    <div className='fs-4 fw-bolder'>
                                        {trakaDetalji?.Sirina || '-'} mm
                                    </div>
                                    <div className='text-muted small mt-2'>Trenutna Duzina</div>
                                    <div className='fs-5 fw-bolder'>{trakaDetalji?.TrenutnaDuzina || '-'} m</div>
                                    <div className='text-muted small mt-2'>Proizvodjac</div>
                                    <div className='fs-5 fw-bolder'>{trakaDetalji?.repromaterijal?.proizvodjac?.PartnerNaziv || '-'}</div>
                                </div>
                            </div>
                        </div>

                        <div className='col-12 col-xl-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body p-4'>
                                    <div className='text-muted fs-4'>Trenutna tezina trake</div>
                                    <div className='fw-semibold'>
                                        <span className='text-success fs-2'>{trakaDetalji?.Tezina || 0}</span> kg
                                    </div>

                                    <div className='text-muted fs-4 mt-3'>Potrebna kolicina po normativu</div>
                                    <div className='fw-semibold'>
                                        <span className='text-primary fs-2'>{potrebnaKolicina.toFixed(2)}</span> kg
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='card shadow-sm border-0 mb-4'>
                        <div className='card-body p-4'>
                            <div className='row g-3 align-items-end'>
                                <div className='col-12 col-lg-4'>
                                    <label className='form-label fw-semibold'>Krajnja tezina trake</label>
                                    <Form.Control
                                        type='number'
                                        size='lg'
                                        value={krajnjaTezina}
                                        onChange={(e) => setKrajnjaTezina(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className='col-12 col-lg-4'>
                                    <label className='form-label fw-semibold'>Lokacija trake</label>
                                    <LokacijeRsTraka
                                        value={lokacija}
                                        onChange={(name: any, value: any) => setLokacija(value)}
                                        errors={undefined}
                                        touched={undefined}
                                    />
                                </div>

                                <div className='col-12 col-lg-4'>
                                    {krajnjaTezina && lokacija && (
                                        <Button className='btn btn-success btn-lg w-100' disabled={saving || komentarObavezan} onClick={handlePotvrdiPromet}>
                                            {saving ? (
                                                <>
                                                    Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2' />
                                                </>
                                            ) : (
                                                <>
                                                    <i className='bi bi-check2-circle me-2' />
                                                    Potvrdi promet trake
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>

                                <div className='col-12'>
                                    <label className='form-label fw-semibold d-block'>Nalog se radi od vise rolni</label>
                                    <div className='d-flex gap-3 flex-wrap'>
                                        <Form.Check
                                            type='radio'
                                            id='traka_vise_rolni_ne'
                                            name='traka_vise_rolni'
                                            label='Ne'
                                            checked={!nalogSeRadiOdViseRolni}
                                            onChange={() => setNalogSeRadiOdViseRolni(false)}
                                        />
                                        <Form.Check
                                            type='radio'
                                            id='traka_vise_rolni_da'
                                            name='traka_vise_rolni'
                                            label='Da'
                                            checked={nalogSeRadiOdViseRolni}
                                            onChange={() => setNalogSeRadiOdViseRolni(true)}
                                        />
                                    </div>
                                </div>

                                {krajnjaTezina && (
                                    <div className='col-12'>
                                        <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2'>
                                            <span className='badge badge-light-primary fs-6'>
                                                Potrosena kolicina: {potrosenaKolicina.toFixed(2)} kg
                                            </span>
                                            {!nalogSeRadiOdViseRolni && prikaziUpozorenje && (
                                                <span className='alert alert-danger py-2 px-3 mb-0 fw-bold'>
                                                    Molim provjerite krajnju kolicinu odstupa 2% od potrebne kolicine
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {!nalogSeRadiOdViseRolni && nemaPotrebneKolicinePoNormativu && (
                                    <div className='col-12'>
                                        <div className='alert alert-danger py-2 px-3 mb-0 fw-bold'>
                                            Nema potrebne kolicine po normativu. Unesite napomenu.
                                        </div>
                                    </div>
                                )}

                                {prikaziNapomenu && (
                                    <div className='col-12'>
                                        <label className='form-label required fw-semibold'>Napomena</label>
                                        <Form.Control
                                            as='textarea'
                                            rows={3}
                                            value={napomena}
                                            onChange={(e) => setNapomena(e.target.value)}
                                            placeholder='Unesite komentar za odstupanje'
                                            required
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='card shadow-sm border-0'>
                        <div className='card-body'>
                            <div className='d-flex align-items-center justify-content-between gap-2 mb-3'>
                                <h3 className='fw-bolder mb-0'>Odabrane stavke za materijal</h3>
                                <div className='text-muted small'>Ukupno stavki: {stavke.length}</div>
                            </div>

                            <div className='table-responsive'>
                                <Table hover className='align-middle mb-0'>
                                    <thead>
                                    <tr className='bg-primary text-white'>
                                        <th className='text-nowrap'>Broj naloga</th>
                                        <th className='text-nowrap'>Id radne stavke</th>
                                        <th className='text-nowrap'>Sifra artikla</th>
                                        <th className='text-nowrap'>Naziv artikla</th>
                                        <th className='text-nowrap'>Potrebna kolicina po normativu</th>
                                        <th className='text-nowrap'>Pocetna kolicina</th>
                                        <th className='text-nowrap'>Utrosak materijala</th>
                                        <th className='text-nowrap'>Krajnja kolicina</th>
                                        <th className='text-nowrap text-center'>Skenirano</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {stavkeSaKolicinama.map(({item, rowKey, pocetnaKolicina, utrosak, krajnjaKolicina}) => (
                                        <tr key={rowKey}>
                                            <td className='fw-semibold text-nowrap'>{getBrojNaloga(item)}</td>
                                            <td className='text-nowrap'>{getStavkaId(item) ?? '-'}</td>
                                            <td className='text-nowrap'>{getSifraArtikla(item)}</td>
                                            <td>{getNazivArtikla(item)}</td>
                                            <td>{item.PotrebnaKolicina ?? '-'}</td>
                                            <td className='fw-semibold text-nowrap'>{pocetnaKolicina}</td>
                                            <td style={{minWidth: 150}}>
                                                <Form.Control
                                                    type='number'
                                                    value={utrosak}
                                                    placeholder={String(item.PotrebnaKolicina ?? '')}
                                                    onChange={(e) => setUtrosakMaterijala((prev) => ({...prev, [rowKey]: e.target.value}))}
                                                />
                                            </td>
                                            <td className='fw-semibold text-nowrap'>
                                                {typeof krajnjaKolicina === 'number' ? Math.round(krajnjaKolicina) : '-'}
                                            </td>
                                            <td className='text-center'>
                                                {Number(item.Skenirano ?? item.Skrenirano) === 1 ? (
                                                    <span className='badge bg-success'>DA</span>
                                                ) : (
                                                    <span className='badge bg-danger'>NE</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default ZaduzeniMaterijalTraka
